import '../style.less';
import EtiquetteCard from '@/components/_common/EtiquetteCard';
import EtiquetteHeader from '@/components/_common/EtiquetteHeader';
import { WorkOrderEtiquetteFields } from '..';
import { useRef, useCallback } from 'react';
import {
  ListStickerWorkOrdersResponse,
  ServicesListType,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { Button } from 'antd';
import SpaceForButton from '@/components/_common/SpaceForButtons';

type EditableField = keyof Omit<ServicesListType, 'id'>;

interface Props {
  index: number;
  fields?: string[];
  showQrCode?: boolean;
  workOrderNickName?: string;
  complementIdentifier?: string;
  setItemServices?: React.Dispatch<
    React.SetStateAction<
      | {
          firstItem: ListStickerWorkOrdersResponse;
          restItems?: ListStickerWorkOrdersResponse[];
        }
      | undefined
    >
  >;
  previewRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
}

export default function EtiquetteWhiteCard({
  fields,
  complementIdentifier,
  previewRefs,
  index,
  setItemServices,
}: Props) {
  const EMPTY_PLACEHOLDER = '_________________________';

  const DEFAULT_DATA: ServicesListType = {
    id: 0,
    serviceName: EMPTY_PLACEHOLDER,
    exitKilometers: EMPTY_PLACEHOLDER,
    nextRevisionInKilometers: EMPTY_PLACEHOLDER,
    returnDate: EMPTY_PLACEHOLDER,
    nextRevisionDate: EMPTY_PLACEHOLDER,
  };

  const dataRef = useRef<ServicesListType>({ ...DEFAULT_DATA });
  const spanRefs = useRef<
    Partial<Record<EditableField, HTMLSpanElement | null>>
  >({});

  const handleBlur = useCallback(
    (field: EditableField, event: React.FocusEvent<HTMLSpanElement>) => {
      dataRef.current[field] =
        event.currentTarget.textContent || EMPTY_PLACEHOLDER;
    },
    [],
  );

  const clearFields = useCallback(() => {
    dataRef.current = { ...DEFAULT_DATA };
    Object.values(spanRefs.current).forEach((span) => {
      if (span) span.textContent = EMPTY_PLACEHOLDER;
    });
  }, []);

  const handleAddService = useCallback(() => {
    setItemServices?.((prev) => {
      if (!prev?.firstItem) return prev;
      return {
        ...prev,
        firstItem: {
          ...prev.firstItem,
          servicesList: [
            ...prev.firstItem.servicesList,
            { ...dataRef.current },
          ],
        },
      };
    });
    clearFields();
  }, [setItemServices, clearFields]);

  const has = (field: WorkOrderEtiquetteFields) => fields?.includes(field);

  const editableField = (field: EditableField, label: string) => (
    <div className="etiquette-review-field">
      <span className="etiquette-review-field-value">{label}</span>{' '}
      <span
        ref={(element) => (spanRefs.current[field] = element)}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={EMPTY_PLACEHOLDER}
        onBlur={(event) => handleBlur(field, event)}
        className="editable"
      />
    </div>
  );

  return (
    <EtiquetteCard
      isEtiquetteWhite
      ref={(element) => (previewRefs.current[index] = element)}
      header={
        has(WorkOrderEtiquetteFields.header) && (
          <EtiquetteHeader
            companyName="Etiqueta em branco"
            complementIdentifier={complementIdentifier}
            showOS={has(WorkOrderEtiquetteFields.complementIdentifier)}
          />
        )
      }
    >
      <div className="etiquette-review-content">
        <div className="etiquette-review-info">
          {has(WorkOrderEtiquetteFields.serviceName) &&
            editableField('serviceName', 'Serviço:')}

          {has(WorkOrderEtiquetteFields.exchangeKm) &&
            editableField('exitKilometers', 'KM da troca:')}

          {has(WorkOrderEtiquetteFields.kmNextChange) &&
            editableField('nextRevisionInKilometers', 'KM próx. troca:')}

          {has(WorkOrderEtiquetteFields.carriedOut) &&
            editableField('returnDate', 'Realizado:')}

          {has(WorkOrderEtiquetteFields.return) &&
            editableField('nextRevisionDate', 'Retorno:')}
        </div>
      </div>

      <div className="etiquette-review-footer">
        <SpaceForButton justifyContent="center">
          <Button type="primary" block onClick={handleAddService}>
            Adicionar etiqueta
          </Button>
        </SpaceForButton>
      </div>
    </EtiquetteCard>
  );
}
